import { Header, createColumnHelper } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { Alert, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { Report } from "../../../api/src/reports/reports.service";
import Loading from "../Loading";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import {
  Table,
  metaDefault,
  useSkipper,
  useTable,
} from "../components/Table/Table";
import { DateRangeFilter, dateFilterFn } from "../components/Table/filters";
import { displayAccountName } from "../lib/display";

type ReportRow = Report & { householdName?: string; accountName?: string };

export const ReportTypeFilter = <TRow, TValue>({
  header,
}: {
  header: Header<TRow, TValue>;
}) => {
  const value = (header.column.getFilterValue() ?? "") as string;

  const onChange = useCallback(
    (ev: React.ChangeEvent<HTMLSelectElement>) => {
      header.column.setFilterValue(ev.target.value);
    },
    [header.column],
  );

  return (
    <Form.Select value={value} onChange={onChange} placeholder="All">
      <option value="">All</option>
      <option value="Household">Household</option>
      <option value="Account">Account</option>
    </Form.Select>
  );
};

const ReportsTable = ({
  reports,
  batchId,
}: {
  reports: Report[];
  batchId?: number;
}) => {
  const columnHelper = useMemo(() => createColumnHelper<ReportRow>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.householdName ?? row.accountName, {
        id: "name",
        cell: (info) =>
          batchId ? (
            <Link
              to={`/reports/${batchId}/${info.row.original.id}`}
              rel="noreferrer"
              target="_blank"
            >
              {info.getValue()}
            </Link>
          ) : (
            info.getValue()
          ),
        header: () => "Name",
        minSize: 225,
      }),
      columnHelper.accessor(
        (row) => (row.accountId === null ? "Household" : "Account"),
        {
          id: "type",
          header: () => "Type",
          filterFn: "equalsString",
          meta: {
            filterComponent: ReportTypeFilter,
          },
        },
      ),
      columnHelper.accessor("reportDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Report Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="reportDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("startDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Start Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="startDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("endDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "End Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="endDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("createdTime", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Created",
        enableColumnFilter: false,
      }),
    ],
    [columnHelper, batchId],
  );

  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds();
  const households = dataHouseholds?.data;

  const {
    isPending: isPendingAccounts,
    isError: isErrorAccounts,
    data: dataAccounts,
  } = useQueryAllAccounts();

  const accounts = dataAccounts?.data;

  const reportsWithHouseholdAccount = useMemo(
    () =>
      (reports ?? []).map((report) => {
        const account = (accounts ?? []).find(
          (account) => account.id === report.accountId,
        );

        return {
          ...report,
          householdName: (households ?? []).find(
            (household) => household.id === report.householdId,
          )?.name,
          accountName: displayAccountName(
            account?.displayName,
            account?.displayNumber,
          ),
        };
      }),
    [accounts, households, reports],
  );

  const [autoResetPageIndex] = useSkipper();

  const { table } = useTable({
    columns,
    data: reportsWithHouseholdAccount ?? [],
    getRowId: (row) => row.id.toString(),
    initialState: {
      sorting: [{ id: "createdTime", desc: true }],
    },
    autoResetPageIndex,
    meta: metaDefault,
  });

  return isPendingHouseholds || isPendingAccounts ? (
    <Loading />
  ) : isErrorHouseholds || isErrorAccounts ? (
    <Alert variant="danger">An error occurred</Alert>
  ) : reports.length <= 0 ? (
    <Alert>No reports found</Alert>
  ) : (
    <Table table={table} />
  );
};

export default ReportsTable;
